// src/App.js
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProjectProvider, useProjects } from './contexts/ProjectContext';
import { PaymentProvider } from './contexts/PaymentContext';

// Layouts
import MainLayout from './components/layouts/MainLayout';
import AuthLayout from './components/layouts/AuthLayout';
import OnboardingLayout from './components/layouts/OnboardingLayout';

// Auth Pages
import Login from './pages/Login';
import Signup from './pages/Signup';

// Dashboard Pages
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import Competitors from './pages/Competitors';
import CompetitorDetail from './pages/CompetitorDetail';
import Subscription from './pages/Subscription';

// Payment Pages
import Checkout from './pages/Checkout';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';
import PaymentFail from './pages/PaymentFail';

// Onboarding Pages
import BusinessPage from './pages/onboarding/Business';
import CompetitorsPage from './pages/onboarding/Competitors';
import BlogPage from './pages/onboarding/Blog';
import ArticlesPage from './pages/onboarding/Article';
import KeywordsPage from './pages/onboarding/Keywords';
import IntegrationPage from './pages/onboarding/Integrations';
import ContentPlanner from './pages/ContentPlanner';
import ContentHistory from './pages/ContentHistory';
import IntegrationsPage from './pages/Integrations';
import NewIntegrationPage from './pages/NewIntegrations';
import LandingPage from './pages/Landing';
import ContentView from './pages/ContentView';
import GeneratedContentView from './pages/GeneratedContentView';
import NotionCallback from './pages/NotionCallback';
import LinkingConfiguration from './pages/LinkingConfiguration';
import { hasCompletedPaymentOrBypass } from './utils/paymentGate';

function App() {
  return (
    <Router>
      <AuthProvider>
        <ProjectProvider>
          <PaymentProvider>
            <Routes>
              <Route path="/" element={<LandingPage />} />

              {/* Auth Routes */}
              <Route element={<AuthLayout />}>
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
              </Route>

              {/* Payment Routes - Now protected by auth */}
              <Route path="/checkout" element={<RequireAuth><Checkout /></RequireAuth>} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              <Route path="/payment/fail" element={<PaymentFail />} />
            
              {/* Onboarding Routes */}
              <Route element={<OnboardingLayout />}>
                <Route path="/onboarding/business" element={<BusinessPage />} />
                <Route path="/onboarding/competitors" element={<CompetitorsPage />} />
                <Route path="/onboarding/blog" element={<BlogPage />} />
                <Route path="/onboarding/articles" element={<ArticlesPage />} />
                <Route path="/onboarding/keywords" element={<KeywordsPage />} />
                <Route path="/onboarding/integrations" element={<IntegrationPage />} />
              </Route>
              
              {/* Protected Routes */}
              <Route element={<MainLayout />}>
                <Route path="/dashboard/content-planner" element={<ContentPlanner />} />
                <Route path="/dashboard/content-history" element={<ContentHistory />} />
                <Route path="/dashboard/competitors" element={<Competitors />} />
                <Route path="/dashboard/competitor/:competitorUrl" element={<CompetitorDetail />} />
                <Route path="/dashboard/integrations" element={<IntegrationsPage />} />
                <Route path="/dashboard/integrations/new" element={<NewIntegrationPage />} />
                <Route path="/dashboard/linking-configuration" element={<LinkingConfiguration />} />
                <Route path="/dashboard/settings" element={<Settings />} />
                <Route path="/dashboard/subscription" element={<Subscription />} />
                <Route path="/dashboard/content/:id" element={<ContentView />} />
                <Route path="/content/:id" element={<GeneratedContentView />} />
              </Route>

              {/* OAuth Callback Routes */}
              <Route path="/notion-callback" element={<NotionCallback />} />

              {/* Default Routes */}
              <Route path="/onboarding" element={<Navigate to="/onboarding/business" replace />} />
              <Route path="/dashboard" element={<Navigate to="/dashboard/content-planner" replace />} />
              
              {/* Catch all redirects to appropriate place based on auth and onboarding status */}
              <Route path="*" element={<AuthRedirect />} />
            </Routes>
          </PaymentProvider>
        </ProjectProvider>
      </AuthProvider>
    </Router>
  );
}

// New component to require authentication
const RequireAuth = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-primary-500 border-t-transparent"></div>
          <div className="mt-4 text-primary-600 text-lg font-medium">Loading...</div>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/signup" replace />;
  }
  
  return children;
};

// Helper component to redirect based on auth and onboarding status
const AuthRedirect = () => {
  const { user, loading: authLoading, onboardingCompleted } = useAuth();
  const { isCurrentProjectOnboarded, loading: projectLoading } = useProjects();
  const [initialLoadComplete, setInitialLoadComplete] = React.useState(false);
  const [redirectTarget, setRedirectTarget] = React.useState(null);
  const [isTabVisible, setIsTabVisible] = React.useState(true);
  
  // Handle tab visibility
  React.useEffect(() => {
    const handleVisibilityChange = () => {
      setIsTabVisible(document.visibilityState === 'visible');
      
      // If coming back to visible tab, update initialLoadComplete
      if (document.visibilityState === 'visible') {
        setInitialLoadComplete(true);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
  
  // Only show loading on first render, not on tab switching
  React.useEffect(() => {
    // If we have cached onboarding status, use it immediately
    const cachedStatus = localStorage.getItem('isCurrentProjectOnboarded');
    
    const isPageReload = window.performance && 
      window.performance.navigation.type === window.performance.navigation.TYPE_RELOAD;
    
    // Only delay if this is a page reload without cached data
    if (isPageReload && !cachedStatus) {
      setTimeout(() => {
        setInitialLoadComplete(true);
      }, 800);
    } else {
      // For normal navigation or when we have cached data, don't add delay
      setInitialLoadComplete(true);
    }
  }, []);
  
  // Determine redirect target with delay to prevent flashing
  React.useEffect(() => {
    // Skip redirect logic when tab is not visible
    if (!isTabVisible) return;
    
    if (!initialLoadComplete) return;
    
    // Use cached values when possible
    const cachedStatus = localStorage.getItem('isCurrentProjectOnboarded');
    const cachedOnboardingCompleted = user ? localStorage.getItem(`onboarding_${user.uid}`) === 'completed' : false;
    
    // Check if onboarding is completed (either from AuthContext or localStorage)
    const isOnboardingComplete = onboardingCompleted || cachedOnboardingCompleted;
    
    if (!user) {
      setRedirectTarget('/login');
    } else if (!hasCompletedPaymentOrBypass()) {
      setRedirectTarget('/checkout');
    } else if (isOnboardingComplete || isCurrentProjectOnboarded === true || cachedStatus === 'true') {
      // If onboarding is marked as complete (from AuthContext) OR project is onboarded, go to dashboard
      setRedirectTarget('/dashboard/content-planner');
    } else if (isCurrentProjectOnboarded === false || cachedStatus === 'false') {
      // If user has paid but not completed onboarding
      const timer = setTimeout(() => {
        if (isCurrentProjectOnboarded !== true && !isOnboardingComplete) {
          setRedirectTarget('/onboarding/business');
        }
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [user, isCurrentProjectOnboarded, onboardingCompleted, initialLoadComplete, isTabVisible]);
  
  // Only show loading on initial page load or when authentication is actually loading
  // Skip loading screen when tab is not visible
  if ((authLoading || projectLoading) && !initialLoadComplete && isTabVisible) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-primary-500 border-t-transparent"></div>
          <div className="mt-4 text-primary-600 text-lg font-medium">Loading...</div>
        </div>
      </div>
    );
  }
  
  // Only redirect when we have a determined target and tab is visible
  if (redirectTarget && isTabVisible) {
    return <Navigate to={redirectTarget} replace />;
  }
  
  // Default loading screen while determining redirect
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="flex flex-col items-center">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-primary-500 border-t-transparent"></div>
        <div className="mt-4 text-primary-600 text-lg font-medium">Loading...</div>
      </div>
    </div>
  );
};

export default App;